/**
 * Routine Checker search ranking utilities.
 *
 * Produces a single, interpretable score per (product, query, slot) so the
 * top suggestion for a user's input is almost always the one they meant.
 *
 *   score = matchQuality * 10 + brandPrestige
 *
 * Match quality tiers (higher wins):
 *   1000  brand exact match               "cerave"       → CeraVe products
 *    900  full-name exact match           "toleriane double repair"
 *    800  brand prefix                    "cera"         → CeraVe
 *    750  normalized-brand prefix         "laroche"      → La Roche-Posay
 *    700  full-name prefix                "cerave foam"  → CeraVe Foaming…
 *    650  name prefix                     "foaming"      → Foaming Cleanser
 *    500  brand substring                 "roche"        → La Roche-Posay
 *    450  full-name substring             "niacin"       → …Niacinamide 10%
 *    300  every query-token found in name "cera foam"    → CeraVe Foaming…
 *    250  typo-tolerant token match       "cerabe foma"  → CeraVe Foaming…
 *    200  fuzzy brand (edit distance ≤ 2) "cerve"        → CeraVe
 *    150  fuzzy product-name head         "tolerine"     → Toleriane
 *      0  no match
 *
 * Brand prestige is a curated weight for widely-recognized US / global
 * skincare brands — breaks ties between equally-good matches in favor of
 * the one a first-time visitor is most likely to mean. Unknown brands get
 * 0 and are ranked purely by match quality.
 */

// Normalize a string for comparison: lowercase, strip punctuation, collapse
// whitespace. Keeps letters, digits, and internal spaces.
function norm(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}\s]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Tighter normalization — also removes all whitespace. Used for "laroche" →
// matches "la roche-posay", "paulaschoice" → "paula's choice", etc.
function tight(s) {
  return norm(s).replace(/\s+/g, "");
}

// Bounded Levenshtein — both inputs are short in this UI (brand or token
// length), so the O(n*m) cost is negligible.
export function editDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    let prev = dp[0];
    dp[0] = j;
    for (let i = 1; i <= a.length; i++) {
      const tmp = dp[i];
      dp[i] = a[i - 1] === b[j - 1]
        ? prev
        : 1 + Math.min(prev, dp[i], dp[i - 1]);
      prev = tmp;
    }
  }
  return dp[a.length];
}

// Curated brand prestige weights. Keys are normalized lowercase brand names.
// Higher = more widely recognized in the US mass / drugstore / derm market.
// Missing brands score 0 and rank purely by match quality.
const BRAND_PRESTIGE = {
  // Drugstore staples
  "cerave":            100,
  "la roche-posay":     98,
  "la roche posay":     98,
  "cetaphil":           95,
  "neutrogena":         92,
  "eucerin":            85,
  "aveeno":             85,
  "vanicream":          80,
  "vaseline":           72,
  "nivea":              68,
  "olay":               70,
  "pond's":             60,
  "ponds":              60,

  // Derm / clinical
  "paula's choice":     92,
  "paulas choice":      92,
  "the ordinary":       92,
  "skinceuticals":      88,
  "differin":           80,
  "eltamd":             88,
  "elta md":            88,
  "first aid beauty":   78,
  "good molecules":     72,
  "bioderma":           78,
  "avène":              78,
  "avene":              78,
  "aquaphor":           75,

  // Sunscreen
  "supergoop":          82,
  "supergoop!":         82,
  "blue lizard":        72,
  "black girl sunscreen": 68,

  // K-Beauty hot brands
  "cosrx":              88,
  "anua":               82,
  "round lab":          80,
  "beauty of joseon":   82,
  "laneige":            80,
  "innisfree":          75,
  "some by mi":         70,
  "isntree":            70,
  "mixsoon":            65,
  "dr. jart+":          72,
  "dr jart":            72,
  "pyunkang yul":       70,

  // Prestige department / indie
  "drunk elephant":     78,
  "glow recipe":        72,
  "summer fridays":     68,
  "glossier":           68,
  "kiehl's":            72,
  "kiehls":             72,
  "clinique":           75,
  "origins":            60,
  "fresh":              58,
  "dermalogica":        72,
  "murad":              65,
  "tatcha":             70,
  "youth to the people": 65,
  "youth to the ppl":   65,
  "versed":             65,
  "bubble":             60,
  "byoma":              72,
  "topicals":           65,
  "naturium":           75,
  "hero cosmetics":     65,
  "hero":               60,

  // Dermatological / Rx-adjacent
  "differin gel":       80,
  "vichy":              72,
  "bioré":              70,
  "biore":              70,
  "hada labo":          65,
};

const BRAND_PRESTIGE_NORMALIZED = (() => {
  const m = new Map();
  for (const [k, v] of Object.entries(BRAND_PRESTIGE)) {
    m.set(norm(k), v);
    m.set(tight(k), v);
  }
  return m;
})();

export function brandPrestige(brand) {
  if (!brand) return 0;
  const n = norm(brand);
  const t = tight(brand);
  return BRAND_PRESTIGE_NORMALIZED.get(n)
      || BRAND_PRESTIGE_NORMALIZED.get(t)
      || 0;
}

// Adaptive edit-distance cap by word length.
//   ≤ 3 chars → 1 typo allowed
//   ≤ 6 chars → 2 typos
//   longer   → up to 3 typos
function fuzzyCap(len) {
  if (len <= 3) return 1;
  if (len <= 6) return 2;
  return 3;
}

// Per-product match-quality score. Returns 0 if no conceivable match.
export function matchQuality(product, query) {
  const q = norm(query);
  if (q.length < 2) return 0;

  const brand = norm(product.brand);
  const name  = norm(product.name);
  const full  = norm(product.full_name) || `${brand} ${name}`.trim();
  const brandT = tight(product.brand);
  const fullT  = tight(product.full_name) || (brandT + tight(product.name));
  const qT     = tight(query);

  // Tier 1 — exact
  if (brand === q) return 1000;
  if (full === q) return 900;

  // Tier 2 — prefix (including whitespace-stripped "laroche" variant)
  if (brand.startsWith(q)) return 800;
  if (brandT.startsWith(qT) && qT.length >= 4) return 750;
  if (full.startsWith(q)) return 700;
  if (name.startsWith(q)) return 650;

  // Tier 3 — substring
  if (brand.includes(q)) return 500;
  if (full.includes(q)) return 450;
  if (fullT.includes(qT) && qT.length >= 5) return 430;

  // Tier 4 — every query token appears somewhere in the full name
  const tokens = q.split(" ").filter((t) => t.length >= 2);
  if (tokens.length > 1 && tokens.every((t) => full.includes(t))) {
    return 300;
  }

  // Tier 5 — token-level typo tolerance for MULTI-token queries only. Every
  // query token either appears literally, or is within a small edit distance
  // of some word in the full name. Catches "cerabe foma" → CeraVe Foaming
  // Cleanser. Single-token fuzziness is handled by tiers 6 & 7 below (which
  // require matching the brand or product-name head specifically, preventing
  // false positives like "foaming" matching any word ending in "-ing").
  const fullWords = full.split(" ").filter(Boolean);
  if (tokens.length > 1) {
    const matched = tokens.filter((t) => {
      if (full.includes(t)) return true;
      const cap = fuzzyCap(t.length);
      return fullWords.some(
        (w) => w.length >= 3 && editDistance(t, w) <= cap,
      );
    });
    if (matched.length === tokens.length) {
      return 250;
    }
  }

  // Tier 6 — fuzzy brand (single-token query close to brand name)
  if (q.indexOf(" ") === -1 && brand) {
    const d = Math.min(
      editDistance(q, brand),
      editDistance(qT, brandT),
    );
    const cap = fuzzyCap(Math.max(brand.length, 4));
    if (d <= cap) return 200 - d * 20;
  }

  // Tier 7 — fuzzy product-name head (single-token query close to first
  // meaningful word of the product name)
  if (q.indexOf(" ") === -1 && name) {
    const head = (name.split(" ").find((w) => w.length >= 3) || name);
    const d = editDistance(q, head);
    if (d <= fuzzyCap(head.length)) return 150 - d * 15;
  }

  return 0;
}

// Rank products for a given query, best first. Caller is responsible for
// pre-filtering to the current slot / category subset.
export function rankProducts(products, query) {
  const scored = [];
  for (const p of products) {
    const mq = matchQuality(p, query);
    if (mq === 0) continue;
    const score = mq * 10 + brandPrestige(p.brand);
    scored.push({ p, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.p);
}

// Sort a list of products by brand prestige descending (breaks ties by
// full_name alphabetical). Used when the query is a broad slot keyword
// like "cleanser" — we show the most recognizable products in that slot
// first, instead of whatever order Mongo returned them in.
export function sortByPrestige(products) {
  return [...products].sort((a, b) => {
    const pa = brandPrestige(a.brand);
    const pb = brandPrestige(b.brand);
    if (pa !== pb) return pb - pa;
    return (a.full_name || a.name || "").localeCompare(
      b.full_name || b.name || "",
    );
  });
}

// Find the closest brand in a list that the user *might* have meant —
// returns `{ brand, distance }` or null.  Used to power the "Did you mean
// Brand?" hint even when we already have results (unlike the old behavior
// which only fired on zero results).
export function suggestBrand(brands, query) {
  const q = norm(query);
  if (q.length < 3 || q.indexOf(" ") !== -1) return null;
  const qT = tight(query);
  let best = null;
  let bestD = Infinity;
  const cap = fuzzyCap(q.length);
  for (const raw of brands) {
    const bn = norm(raw);
    const bt = tight(raw);
    if (!bn) continue;
    // Already matches as prefix — no suggestion needed.
    if (bn.startsWith(q) || bt.startsWith(qT)) return null;
    const d = Math.min(editDistance(q, bn), editDistance(qT, bt));
    if (d < bestD && d <= cap) {
      best = raw;
      bestD = d;
    }
  }
  return best ? { brand: best, distance: bestD } : null;
}
